import { useState, useEffect } from 'react';
import { useApolloClient, useMutation, useQuery } from 'react-apollo';
import uniqBy from 'lodash.uniqby';

import { checkout as checkoutMutation } from '../../../../../graphql/mutations/checkout';
import {
  AlbumFolderWithPurchasedEventAddons,
  EventAddonItems,
} from '../../../../../graphql/queries/events';
import { sentryCaptureMessage } from '../../../../../util/sentry';
import { MeQuery } from '../../../../../graphql/queries/account';
import useAuthenticated from '../../../../../hooks/useAuthenticated';
import { formatPriceForDisplay } from '../../../../../util/format-money';
import { EVENT_ADDON_ITEM_TYPE } from '../../../../../constants/event-items';
import {
  AlbumFolderForPurchasedEventAddonFeature,
  AlbumForPurchasedEventAddonFeature,
} from '../../../services/purchasedEventAddonFeature/hooks/graphql/queries';
import { PURCHASE_TYPE } from '../../../../../components/global/ThankYouView/constants';
import {
  isIOSNativeAppWebView,
  isNativeAppWebView,
} from '../../../../../services/native-bridge/native-apps-interface';
import { sortEventAddonItemsByPriority } from '../../../views/AlbumFolder/FreemiumEventCreateWizard/util';
import {
  isEligibleForShutterbugFreeTrial,
  SEARCH_SUBSCRIPTION_PLAN_BILLING_PERIOD,
} from '../../../../temp/views/onboarding/PhotoSearchCheckout/helpers';
import useOpenModal from '../../../../../hooks/useOpenModal';
import { THANK_YOU_MODAL } from '../../../../../components/global/ThankYouView';
import useCrazyEggConversions from '../../../../../services/crazy-egg/hooks/useCrazyEggConversions';
import { CRAZY_EGG_CONVERSION_EVENTS } from '../../../../../services/crazy-egg/constants';
import { gtm } from '../../../../../util/analytics';
import { ALBUM_SUBTYPE } from '../../../../../constants/albums';
import useBasename from '../../../../../components/routing/useBasename';
import { FREEMIUM_EVENT_CREATE_WALDOFIED_STEP_VARIANTS } from '../../../../shutterbug/views/SignupAlbumCreate/helpers';
import {
  PINTEREST_CATEGORIES,
  PINTEREST_EVENTS,
} from '../../../../../services/pinterest/constants';
import usePinterestTracking from '../../../../../services/pinterest/hooks/usePinterestTracking';

/**
 * General Event Addons hook
 *
 * @param {Object} options The options.
 * @param {Object?} options.account The Account object.
 * @param {String?} options.albumId The album id.
 * @param {String?} options.albumFolderId The albumFolder id.
 * @param {Boolean?} options.useEntityAvailableAddonItems If set, the entity id will be used when fetching available eventAddonItems
 * @param {Array<String>?} options.defaultSelectedEventAddonItemIds List of event addon items to be pre-selected.
 * @param {Array<String>?} options.defaultSelectedEventAddonItemTypes List of event addon items types to be pre-selected.
 * @param {Object?} options.eventAddonItemsQueryVars Object containing variables regarding event addon items.
 * @param {Array<String>?} options.defaultEventAddonItemTypes List of event addon item types to be included in eventAddonItems
 * @param {Function} [options.onCheckoutSuccess] Method running on checkout success.
 * @param {Function} [options.onCheckoutError] Method running on checkout error.
 * @returns {{onCheckout: ((function({token: *, albumFolderId?: *}=): Promise<void>)|*), submitting: boolean, checkoutError: string, onEventAddonItemToggle: onEventAddonItemToggle, success: boolean, eventAddonItems: *[], loadingEventAddonItems: boolean, selectedEventAddonItemIds: *[]}}
 */
const useEventAddonPurchase = ({
  account,
  onCheckoutSuccess,
  onCheckoutError,
  albumId,
  albumFolderId,
  useEntityAvailableAddonItems,
  defaultSelectedEventAddonItemIds = [],
  defaultSelectedEventAddonItemTypes = [],
  eventAddonItemsQueryVars = {},
  defaultEventAddonItemTypes = [EVENT_ADDON_ITEM_TYPE.WALDOFIED],
} = {}) => {
  const authenticated = useAuthenticated();
  const { data: { me: user } = {}, refetch: refetchAccount } = useQuery(
    MeQuery,
    {
      skip: !authenticated,
    },
  );
  const openModal = useOpenModal();
  const basename = useBasename();
  const crazyEggConversions = useCrazyEggConversions();
  const { onTrackEvent: onTrackPinterestEvent } = usePinterestTracking();
  const client = useApolloClient();
  const [submitting, setSubmitting] = useState(false);
  const [checkoutError, setCheckoutError] = useState('');
  const [success, setSuccess] = useState(false);
  const [defaultTypesSet, setDefaultTypesSet] = useState(false);
  const [selectedEventAddonItemIds, setSelectedEventAddonItemIds] = useState(
    defaultSelectedEventAddonItemIds ?? [],
  );
  const isIOSWebView = isIOSNativeAppWebView();

  const {
    data: { eventAddonItems = [] } = {},
    loading: loadingEventAddonItems,
  } = useQuery(EventAddonItems, {
    variables: {
      ...eventAddonItemsQueryVars,
      hierarchy_item_id: useEntityAvailableAddonItems
        ? albumFolderId ?? albumId
        : undefined,
      platform: isIOSWebView ? 'IOS' : 'WEB',
    },
  });

  const {
    data: { eventAddonItems: iOSEventAddonItemsBundles = [] } = {},
  } = useQuery(EventAddonItems, {
    variables: {
      event_addon_item_id: '19',
      include_event_addon_bundles: true,
      platform: 'IOS',
    },
    skip: !isIOSWebView,
  });

  useEffect(() => {
    if (defaultTypesSet) {
      return;
    }

    if (
      eventAddonItems?.length > 0 &&
      defaultSelectedEventAddonItemTypes?.length > 0
    ) {
      setSelectedEventAddonItemIds(
        eventAddonItems
          .filter(
            ({ types }) =>
              types.length === 1 &&
              defaultSelectedEventAddonItemTypes.includes(types[0]),
          )
          .map(({ id }) => id),
      );
      setDefaultTypesSet(true);
    }
  }, [eventAddonItems]);

  const { data: { album } = {} } = useQuery(
    AlbumForPurchasedEventAddonFeature,
    {
      variables: {
        id: albumId,
      },
      skip: !albumId,
    },
  );

  const { data: { albumFolder } = {} } = useQuery(
    AlbumFolderForPurchasedEventAddonFeature,
    {
      variables: {
        id: albumFolderId,
      },
      skip: !albumFolderId,
    },
  );

  const entity = albumFolder ?? album;

  const [checkout] = useMutation(checkoutMutation, {
    refetchQueries: (() => {
      return [
        albumId
          ? {
              query: AlbumForPurchasedEventAddonFeature,
              variables: {
                id: albumId,
              },
            }
          : null,
        albumFolderId
          ? {
              query: AlbumFolderForPurchasedEventAddonFeature,
              variables: {
                id: albumFolderId,
              },
            }
          : null,
        useEntityAvailableAddonItems
          ? {
              query: EventAddonItems,
              variables: {
                hierarchy_item_id: albumFolderId ?? albumId,
              },
            }
          : null,
      ].filter(Boolean);
    })(),
  });

  const onCheckout = async ({
    token,
    albumFolderId: checkoutAlbumFolderId,
    eventType = entity?.album_subtype,
    user: loggedUser = user,
    eventAddonItemIds = selectedEventAddonItemIds,
    searchSubscriptionItem,
    waldofiedStepVariant,
    ...passThroughArgs
  } = {}) => {
    if (submitting) {
      return;
    }

    try {
      setSubmitting(true);
      setCheckoutError('');

      const { data: { checkout: checkoutResponse } = {} } = await checkout({
        variables: {
          input: {
            account_id: account?.id ?? loggedUser?.id,
            stripe_token: token?.id,
            stripe_payment_method_id: token?.stripe_payment_method_id,
            stripe_connect_account_id: token?.stripe_connect_account_id,
            email_address:
              token?.email ??
              account?.email_address ??
              loggedUser?.email_address,
            promo_code: token?.promo_code,
            search_subscription_items: searchSubscriptionItem
              ? [
                  {
                    search_subscription_item_id: searchSubscriptionItem.id,
                    skip_free_trial: !loggedUser?.can_create_free_event_album_folder,
                  },
                ]
              : [],
            event_addon_items: eventAddonItemIds.map(eventAddonItemId => ({
              event_addon_item_id: eventAddonItemId,
              quantity: 1,
              album_folder_id: checkoutAlbumFolderId,
            })),
            platform: 'WEB',
          },
        },
      });

      const {
        data: { albumFolder: albumFolderWithPurchasedEventAddons },
      } = await client.query({
        query: AlbumFolderWithPurchasedEventAddons,
        variables: {
          id: checkoutAlbumFolderId,
        },
      });

      if (searchSubscriptionItem) {
        refetchAccount?.();

        if (!isNativeAppWebView()) {
          if (
            searchSubscriptionItem.trial_days > 0 &&
            isEligibleForShutterbugFreeTrial(loggedUser)
          ) {
            gtm.trackCustomEvent({
              event: 'Plus_Trial_Purchased',
              consumer_subtype: eventType ?? 'CUSTOM',
              user_id: account?.id ?? user?.id,
              price: 0,
            });
            onTrackPinterestEvent({
              event: PINTEREST_EVENTS.CHECKOUT,
              data: {
                order_id: checkoutResponse?.transaction?.id,
                currency: 'USD',
                content_name: 'Plus_Trial_Purchased',
                content_category: PINTEREST_CATEGORIES.SUBSCRIPTION,
                num_items: 1,
                value: 0,
              },
            });
          } else if (
            searchSubscriptionItem.billing_period ===
            SEARCH_SUBSCRIPTION_PLAN_BILLING_PERIOD.MONTHLY
          ) {
            gtm.trackCustomEvent({
              event: 'Plus_Monthly_Purchased',
              consumer_subtype: 'CONSUMER',
              user_id: account?.id ?? user?.id,
              price: formatPriceForDisplay(
                checkoutResponse?.total_price?.value,
              ),
            });
            onTrackPinterestEvent({
              event: PINTEREST_EVENTS.CHECKOUT,
              data: {
                order_id: checkoutResponse?.transaction?.id,
                currency: 'USD',
                content_name: 'Plus_Monthly_Purchased',
                content_category: PINTEREST_CATEGORIES.SUBSCRIPTION,
                num_items: 1,
                value: formatPriceForDisplay(
                  checkoutResponse?.total_price?.value,
                ),
              },
            });
          } else if (
            searchSubscriptionItem.billing_period ===
            SEARCH_SUBSCRIPTION_PLAN_BILLING_PERIOD.YEARLY
          ) {
            gtm.trackCustomEvent({
              event: 'Plus_Annual_Purchased',
              consumer_subtype: 'CONSUMER',
              user_id: account?.id ?? user?.id,
              price: formatPriceForDisplay(
                checkoutResponse?.total_price?.value,
              ),
            });
            onTrackPinterestEvent({
              event: PINTEREST_EVENTS.CHECKOUT,
              data: {
                order_id: checkoutResponse?.transaction?.id,
                currency: 'USD',
                content_name: 'Plus_Monthly_Purchased',
                content_category: PINTEREST_CATEGORIES.SUBSCRIPTION,
                num_items: 1,
                value: formatPriceForDisplay(
                  checkoutResponse?.total_price?.value,
                ),
              },
            });
          }

          const purchaseMetadata = {
            subscriptionDetails: {
              searchSubscriptionItem,
              freeTrialAvailable:
                searchSubscriptionItem.trial_days > 0 &&
                isEligibleForShutterbugFreeTrial(loggedUser),
            },
            verticalDetails: {
              persona: undefined,
              basename: 'consumer',
            },
          };

          openModal(THANK_YOU_MODAL, {
            purchaseType: PURCHASE_TYPE.SEARCH_SUBSCRIPTION,
            purchaseMetadata,
          });
        }
      }
      const purchasedEventAddons =
        albumFolderWithPurchasedEventAddons?.purchased_event_addons?.records;

      const purchasedEventAddonItems = eventAddonItemIds
        .map(eventAddonItemId =>
          eventAddonItems.find(
            eventAddonItem => eventAddonItemId === eventAddonItem.id,
          ),
        )
        .filter(Boolean);

      if (purchasedEventAddonItems.length > 0) {
        if (!isNativeAppWebView()) {
          const transactionEventAddonTypes = uniqBy(
            sortEventAddonItemsByPriority(purchasedEventAddonItems)
              .map(({ types }) => types)
              .reduce((currentTypes, types) => [...currentTypes, ...types], []),
          );

          if (token?.promo_code === 'PLATINUM3FOR2') {
            gtm.trackCustomEvent({
              event: 'Bundle_Purchased',
              consumer_subtype: eventType ?? 'CUSTOM',
              user_id: account?.id ?? user?.id,
              price: formatPriceForDisplay(
                checkoutResponse?.total_price?.value,
              ),
            });
            onTrackPinterestEvent({
              event: PINTEREST_EVENTS.CHECKOUT,
              data: {
                order_id: checkoutResponse?.transaction?.id,
                currency: 'USD',
                content_name: 'Bundle_Purchased',
                content_category: PINTEREST_CATEGORIES.EVENT_ADDON,
                content_brand: eventType ?? 'CUSTOM',
                num_items: 3,
                value: formatPriceForDisplay(
                  checkoutResponse?.total_price?.value,
                ),
              },
            });
          } else {
            transactionEventAddonTypes.forEach(transactionEventAddonType => {
              const isProItem = [
                EVENT_ADDON_ITEM_TYPE.WALDOFIED_PHOTO_FINDER,
                EVENT_ADDON_ITEM_TYPE.WALDOFIED_PHOTO_FINDER_PLUS,
              ].includes(transactionEventAddonType);

              const gtmEvent = (() => {
                if (
                  transactionEventAddonType === EVENT_ADDON_ITEM_TYPE.WALDOFIED
                ) {
                  return 'Waldofied_Purchased';
                }

                if (
                  transactionEventAddonType ===
                  EVENT_ADDON_ITEM_TYPE.WALDOFIED_PHOTO_FINDER
                ) {
                  return 'Single_Event_License_1k';
                }

                if (
                  transactionEventAddonType ===
                  EVENT_ADDON_ITEM_TYPE.WALDOFIED_PHOTO_FINDER_PLUS
                ) {
                  return 'Single_Event_License_3k';
                }

                if (
                  transactionEventAddonType ===
                  EVENT_ADDON_ITEM_TYPE.MEMORY_BOOK
                ) {
                  return 'Memories_Slideshow_Purchased';
                }

                if (
                  [ALBUM_SUBTYPE.WEDDINGS, ALBUM_SUBTYPE.MEMORIALS].includes(
                    eventType,
                  )
                ) {
                  return 'Video_Guestbook_Purchased';
                }

                return 'Group_Video_Purchased';
              })();

              gtm.trackCustomEvent({
                event: gtmEvent,
                consumer_subtype: !isProItem
                  ? eventType ?? 'CUSTOM'
                  : undefined,
                pro_subtype: isProItem ? basename.toUpperCase() : undefined,
                user_id: account?.id ?? user?.id,
                transaction_id: checkoutResponse?.transaction?.id,
                price: formatPriceForDisplay(
                  checkoutResponse?.total_price?.value /
                    transactionEventAddonTypes.length,
                ),
              });
              onTrackPinterestEvent({
                event: PINTEREST_EVENTS.CHECKOUT,
                data: {
                  order_id: checkoutResponse?.transaction?.id,
                  currency: 'USD',
                  content_name: gtmEvent,
                  content_category: PINTEREST_CATEGORIES.EVENT_ADDON,
                  content_brand: eventType ?? 'CUSTOM',
                  num_items: 1,
                  value: formatPriceForDisplay(
                    checkoutResponse?.total_price?.value /
                      transactionEventAddonTypes.length,
                  ),
                },
              });
            });
          }

          const purchaseMetadata = {
            eventAddonDetails: {
              eventAddonItemTypes: transactionEventAddonTypes,
            },
            verticalDetails: {
              persona: undefined,
              basename: 'consumer',
            },
          };

          if (token?.promo_code === 'GOLD25') {
            crazyEggConversions.onTrackConversionEvent(
              CRAZY_EGG_CONVERSION_EVENTS.GOLD25_USED,
            );
          } else if (token?.promo_code === 'PLATINUM3FOR2') {
            crazyEggConversions.onTrackConversionEvent(
              CRAZY_EGG_CONVERSION_EVENTS.GOLD2FOR1_USED,
            );
          }

          transactionEventAddonTypes.forEach(type => {
            if (type === EVENT_ADDON_ITEM_TYPE.WALDOFIED) {
              crazyEggConversions.onTrackConversionEvent(
                CRAZY_EGG_CONVERSION_EVENTS.PURCHASED_GOLD,
              );

              if (
                waldofiedStepVariant ===
                FREEMIUM_EVENT_CREATE_WALDOFIED_STEP_VARIANTS.VARIANT_A
              ) {
                crazyEggConversions.onTrackConversionEvent(
                  CRAZY_EGG_CONVERSION_EVENTS.GOLD_VARIANT_A_PURCHASE,
                );
              } else if (
                waldofiedStepVariant ===
                FREEMIUM_EVENT_CREATE_WALDOFIED_STEP_VARIANTS.VARIANT_B
              ) {
                crazyEggConversions.onTrackConversionEvent(
                  CRAZY_EGG_CONVERSION_EVENTS.GOLD_VARIANT_B_PURCHASE,
                );
              }
            }
            if (type === EVENT_ADDON_ITEM_TYPE.MEMORY_BOOK) {
              crazyEggConversions.onTrackConversionEvent(
                CRAZY_EGG_CONVERSION_EVENTS.PURCHASED_MSS,
              );
            }
            if (type === EVENT_ADDON_ITEM_TYPE.GROUP_VIDEO) {
              crazyEggConversions.onTrackConversionEvent(
                CRAZY_EGG_CONVERSION_EVENTS.PURCHASED_VGB,
              );
            }
          });

          openModal(THANK_YOU_MODAL, {
            purchaseType: PURCHASE_TYPE.EVENTS,
            purchaseMetadata,
          });
        }
      }

      setSubmitting(false);
      setSuccess(true);
      setSelectedEventAddonItemIds([]);
      onCheckoutSuccess?.({
        purchasedEventAddons,
        purchasedEventAddonItems,
        user: loggedUser,
        ...passThroughArgs,
      });
    } catch (error) {
      setCheckoutError(error);
      onCheckoutError?.({ error, ...passThroughArgs });
      sentryCaptureMessage(error, { level: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const onEventAddonItemToggle = eventAddonItemId => {
    if (selectedEventAddonItemIds.includes(eventAddonItemId)) {
      setSelectedEventAddonItemIds(
        selectedEventAddonItemIds.filter(id => id !== eventAddonItemId),
      );
    } else {
      setSelectedEventAddonItemIds([
        ...selectedEventAddonItemIds,
        eventAddonItemId,
      ]);
    }
  };

  const onEventAddonItemRemove = eventAddonItemIdOrIds => {
    if (Array.isArray(eventAddonItemIdOrIds)) {
      setSelectedEventAddonItemIds(
        selectedEventAddonItemIds.filter(
          id => !eventAddonItemIdOrIds.includes(id),
        ),
      );

      return;
    }

    if (selectedEventAddonItemIds.includes(eventAddonItemIdOrIds)) {
      setSelectedEventAddonItemIds(
        selectedEventAddonItemIds.filter(id => id !== eventAddonItemIdOrIds),
      );
    }
  };

  return {
    checkoutError,
    submitting,
    success,
    onCheckout,
    eventAddonItems: eventAddonItems.filter(
      item =>
        defaultEventAddonItemTypes.includes(item.types[0]) ||
        // Ensures we always have the pre-selected item if required
        defaultSelectedEventAddonItemIds.includes(item.id),
    ),
    purchasedAddonItems:
      (album ?? albumFolder)?.purchased_event_addons?.records ?? [],
    iOSEventAddonItemsBundle: iOSEventAddonItemsBundles?.[0],
    loadingEventAddonItems,
    selectedEventAddonItemIds,
    setSelectedEventAddonItemIds,
    onEventAddonItemToggle,
    onEventAddonItemRemove,
  };
};

export default useEventAddonPurchase;
